import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { LANGUAGES } from "@/constants/site";

export default function LanguageSwitcher() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = lang && ["en", "fr", "de", "es", "pt"].includes(lang) ? lang : "en";

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const switchTo = (code: string) => {
    setOpen(false);
    const segments = location.pathname.split("/").filter(Boolean);
    if (segments.length > 0) {
      segments[0] = code;
    } else {
      segments.unshift(code);
    }
    navigate(`/${segments.join("/")}`);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-md border border-background-300/60 bg-background-50 px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <i className="ri-global-line flex h-4 w-4 items-center justify-center text-base leading-none" />
        <span className="whitespace-nowrap uppercase">{current}</span>
        <i className="ri-arrow-down-s-line flex h-4 w-4 items-center justify-center text-base leading-none" />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 z-50 mt-2 w-44 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1 shadow-sm"
        >
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              role="option"
              aria-selected={l.code === current}
              onClick={() => switchTo(l.code)}
              className={`flex w-full items-center justify-between whitespace-nowrap px-4 py-2 text-left text-sm transition-colors hover:bg-background-100 ${
                l.code === current ? "font-semibold text-primary-700" : "text-foreground-700"
              }`}
            >
              <span>{t(l.labelKey)}</span>
              {l.code === current && (
                <i className="ri-check-line flex h-4 w-4 items-center justify-center text-primary-600" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}